import type { LandingSection } from '@easyflow/shared';

export interface SeoMeta {
  title?: string;
  description?: string;
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  focusKeyword?: string;
  customCanonical?: string;
  ogLocale?: string;
  [key: string]: unknown;
}

export interface PageSettings {
  primaryColor?: string;
  accentColor?: string;
  colorMode?: 'light' | 'dark' | 'auto';
  custom_head_code?: string;
  custom_body_code?: string;
  custom_css?: string;
  robotsNoIndex?: boolean;
  businessType?: string;
  businessAddress?: string;
  businessPhone?: string;
  businessHours?: string;
  language?: string;
  faviconUrl?: string;
  temperature?: number;
  [key: string]: unknown;
}

export interface LandingPage {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  status: 'draft' | 'published' | 'archived';
  sections: LandingSection[];
  seo_meta: SeoMeta;
  settings: PageSettings;
  site_group_id: string | null;
  parent_id: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  sections?: LandingSection[];
  phase?: 'analysis' | 'proposal' | 'generation';
}

export type ViewMode = 'list' | 'editor' | 'canvas';

export interface WidgetConfigState {
  bubbleColor: string;
  bubblePosition: 'bottom-right' | 'bottom-left';
  bubbleIcon: 'chat' | 'headphones' | 'mic' | 'phone' | 'sparkles' | 'bot';
  bubbleSize: '40' | '52' | '56' | '64';
  animation: 'bounce' | 'pulse' | 'slide-up' | 'none';
  sound: boolean;
  tooltip: string;
  vibration: boolean;
  autoOpenDelay: '0' | '3' | '5' | '10';
  vaVariant: '' | 'compact' | 'expanded' | 'fullscreen';
  vaPosition: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  vaFirstMessage: string;
  vaAvatarImage: string;
  vaAvatarOrbColor1: string;
  vaAvatarOrbColor2: string;
  vaShowAvatarCollapsed: boolean;
  vaLanguage: string;
  vaOverridePrompt: string;
  vaOverrideVoiceId: string;
  vaOverrideSpeed: string;
  vaOverrideStability: string;
  vaOverrideSimilarityBoost: string;
  vaTextInput: boolean;
  vaMicMuting: boolean;
  vaDismissible: boolean;
  vaDefaultExpanded: boolean;
  vaAlwaysExpanded: boolean;
  vaTranscript: boolean;
  vaOverrideTextOnly: boolean;
  vaCollectFeedback: boolean;
  vaShowAgentStatus: boolean;
  vaDynamicVars: { key: string; value: string }[];
  vaTextContents: Record<string, string>;
  gvColor: string;
  gvPosition: 'bottom-right' | 'bottom-left';
  gvCtaText: string;
  gvShape: 'circle' | 'square';
  gvLiveTranscript: boolean;
  gvShowMute: boolean;
  gvOverrideFirstMessage: string;
  gvOverridePrompt: string;
}

export interface WidgetConfigDialogState {
  open: boolean;
  type: 'chatbot_embed' | 'voice_agent_embed' | 'gemini_voice_embed';
  widgetId: string;
  widgetName: string;
  editMode?: boolean;
  editIndex?: number;
}

export const SECTION_TYPE_LABELS: Record<string, string> = {
  navbar: 'Navbar',
  hero: 'Hero',
  text: 'Testo',
  features: 'Features',
  testimonials: 'Testimonial',
  cta: 'CTA',
  pricing: 'Prezzi',
  faq: 'FAQ',
  contact: 'Contatti',
  stats: 'Statistiche',
  gallery: 'Galleria',
  form_embed: 'Form CRM',
  task_form_embed: 'Form Ticketing',
  appointment_form_embed: 'Form Prenotazione',
  chatbot_embed: 'Chatbot',
  voice_agent_embed: 'Voice Agent',
  gemini_voice_embed: 'Gemini Voice',
  sms_button: 'SMS',
  whatsapp_button: 'WhatsApp',
  logos: 'Loghi',
  video: 'Video',
  team: 'Team',
  timeline: 'Timeline',
  countdown: 'Countdown',
};

export function generateSectionId(type: string, existingSections: LandingSection[]): string {
  const existingIds = new Set(existingSections.map(s => s.id));
  if (!existingIds.has(type)) return type;
  let counter = 2;
  while (existingIds.has(`${type}_${counter}`)) counter++;
  return `${type}_${counter}`;
}

export function insertAfterSection(sections: LandingSection[], newSection: LandingSection, afterId?: string | null): LandingSection[] {
  if (!afterId) return [...sections, newSection];
  const idx = sections.findIndex(s => s.id === afterId);
  if (idx === -1) return [...sections, newSection];
  return [...sections.slice(0, idx + 1), newSection, ...sections.slice(idx + 1)];
}

export const DEFAULT_WIDGET_CONFIG: WidgetConfigState = {
  bubbleColor: '#6366f1',
  bubblePosition: 'bottom-right',
  bubbleIcon: 'chat',
  bubbleSize: '56',
  animation: 'bounce',
  sound: true,
  tooltip: 'Chatta con noi!',
  vibration: true,
  autoOpenDelay: '0',
  vaVariant: '',
  vaPosition: 'bottom-right',
  vaFirstMessage: '',
  vaAvatarImage: '',
  vaAvatarOrbColor1: '',
  vaAvatarOrbColor2: '',
  vaShowAvatarCollapsed: false,
  vaLanguage: 'it',
  vaOverridePrompt: '',
  vaOverrideVoiceId: '',
  vaOverrideSpeed: '',
  vaOverrideStability: '',
  vaOverrideSimilarityBoost: '',
  vaTextInput: false,
  vaMicMuting: false,
  vaDismissible: false,
  vaDefaultExpanded: false,
  vaAlwaysExpanded: false,
  vaTranscript: false,
  vaOverrideTextOnly: false,
  vaCollectFeedback: false,
  vaShowAgentStatus: false,
  vaDynamicVars: [],
  vaTextContents: {},
  gvColor: '#4285f4',
  gvPosition: 'bottom-right',
  gvCtaText: 'Parla con noi',
  gvShape: 'circle',
  gvLiveTranscript: false,
  gvShowMute: true,
  gvOverrideFirstMessage: '',
  gvOverridePrompt: '',
};
